/**
 * NewGalleryDialog — the studio's "new gallery" flow.
 *
 * GAL-11 · three steps, full-screen, one idea per screen:
 *
 *   1. Design    — choose one of the gallery designs. GALLERY-2: Basic
 *                  (00), the default, is picked on arrival — it is also
 *                  the DB default for anything that skips the picker.
 *                  Continue still stays off unless a built design is
 *                  picked, so every gallery made here has a real look.
 *   2. Details   — name, shoot date, optional drop of files, Nova's
 *                  project match, the project picker. This is the
 *                  GAL-5/GAL-8 flow lifted as-is: EXIF → match →
 *                  background upload manager → editor.
 *   3. Customize — the curated controls (accent, background, type,
 *                  Basic's cover, slideshow), an optional access code,
 *                  "Publish now", and a live preview of the design in
 *                  the studio's choices.
 *
 * Create writes the row WITH template + customization (+ the code),
 * enqueues any files, and lands in the editor. The `{ open, onClose }`
 * API is unchanged so the Today dashboard and QuickActions keep
 * mounting it; `initialTemplate` lets the hub's Designs rail start
 * the flow on a design, and `initialProjectId` lets the project
 * workspace skip the picker.
 *
 * DEMO-4 — under Preview Studio the picker lists the demo cast and
 * Create builds the gallery in the session store (see galleries.ts).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, Sparkles, X, ImageIcon, Film, CalendarDays, ArrowRight, ArrowLeft, Check, Globe,
} from 'lucide-react';
import { OrbitIcon } from '@/components/icons/OrbitIcon';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useOrgId } from '@/hooks/useOrgId';
import { useAuth } from '@/hooks/useAuth';
import { useDemoMode } from '@/contexts/DemoModeContext';
import { useDemoStore } from '@/contexts/DemoStoreContext';
import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useDarkScopeClass } from '@/hooks/useThemeMode';
import { cn } from '@/lib/utils';
import { galleryUploads } from '@/lib/uploads/galleryUploadManager';
import { VIDEO_SHIPPED, VIDEO_HELD_MESSAGE, STANDALONE_GALLERIES } from '@/config/launch';
import { useCreateGallery, useAddGalleryPhoto } from '@/services/galleries';
import { useOrganization } from '@/services/organizations';
import {
  readExifDateFromFile,
  useNovaProjectMatch,
  type ProjectMatch,
} from '@/services/novaDelivery';
import { NovaSpinner } from '@/components/nova';
import { DEFAULT_TEMPLATE_ID, GALLERY_THEMES, getGalleryTheme, pickerFontsHref } from './templates/themes';
import { useGoogleFonts } from './templates/useGoogleFonts';
import { isTemplateBuilt } from './templates/GalleryTemplateRenderer';
import type { GalleryCustomization, GalleryTemplateId } from './templates/types';
import { GalleryCustomizeControls } from './design/GalleryCustomizeControls';
import { GalleryCoverFlow } from '@/components/marketing/GalleryCoverFlow';
import { AccessCodeField } from './design/AccessCodeField';
import { DesignPreview } from './design/DesignPreview';
import type { ThumbPhoto } from './design/GalleryDesignThumb';
import { asDay } from '@/lib/dates';

interface NewGalleryDialogProps {
  open: boolean;
  onClose: () => void;
  /** Start on a chosen design (the hub's Designs rail). */
  initialTemplate?: GalleryTemplateId | null;
  /** Pre-attach to a project (the project workspace). */
  initialProjectId?: string | null;
}

type Step = 1 | 2 | 3;

const STEPS: Array<{ n: Step; label: string }> = [
  // GALLERY-ONE — step 1 asks nothing now (see the body); "Design"
  // was the label for the picker that used to be there.
  { n: 1, label: 'Start' },
  { n: 2, label: 'Details' },
  { n: 3, label: 'Customize' },
];

// GAL-4 — video detection. Some browsers hand over an empty MIME
// type for .mov drops, so fall back to extension sniffing.
const isVideoFile = (f: File) =>
  f.type.startsWith('video/') || /\.(mp4|mov|m4v|webm|avi|mkv)$/i.test(f.name);

interface StudioProject {
  id: string;
  name: string;
  scheduled_date: string | null;
  clients: { name: string | null } | null;
}

/**
 * GAL-14 — Radix's Select refuses an empty-string item value (it uses
 * '' internally to mean "nothing selected"), so "No project" needs a
 * sentinel of its own. It never leaves this file: `create` maps it
 * back to null before the row is written.
 */
const NO_PROJECT = '__none__';

/**
 * GAL-14 — how many of the studio's own photos the design previews
 * are built from. The sample album is five; a design that lays out a
 * contact sheet wants a few more than that to read honestly, and every
 * extra one is a full-resolution decode held in memory behind an
 * object URL while the dialog is open. Eight is the compromise.
 */
const PREVIEW_PHOTO_LIMIT = 8;

/**
 * Formats a browser will actually paint from an object URL. HEIC is the
 * reason this exists: Safari on the Mac renders it, nothing else does,
 * and a preview that silently shows blank frames for a studio shooting
 * HEIC is worse than one that quietly falls back to the sample album.
 */
const isPreviewableImage = (f: File) =>
  /^image\/(jpeg|png|webp|gif|avif)$/i.test(f.type) ||
  /\.(jpe?g|png|webp|gif|avif)$/i.test(f.name);

export function NewGalleryDialog({
  open,
  onClose,
  initialTemplate = null,
  initialProjectId = null,
}: NewGalleryDialogProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: orgId } = useOrgId();
  const { isDemo } = useDemoMode();
  const demoStore = useDemoStore();
  const createGallery = useCreateGallery();
  const addPhoto = useAddGalleryPhoto();
  const { hasFeature, openUpgradeModal } = useFeatureAccess();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const darkScope = useDarkScopeClass();
  const { data: org } = useOrganization();
  const brandColor: string | null =
    (org as { brand_color?: string | null } | null | undefined)?.brand_color ?? null;
  const orgLogoUrl: string | null =
    (org as { brand_logo_url?: string | null; logo_url?: string | null } | null | undefined)?.brand_logo_url ??
    (org as { logo_url?: string | null } | null | undefined)?.logo_url ??
    null;

  // Every design's faces, so the picker + preview show real type.
  useGoogleFonts(pickerFontsHref());

  // ── Step ────────────────────────────────────────────────────────
  const [step, setStep] = useState<Step>(1);

  // ── Step 1 ──────────────────────────────────────────────────────
  // GALLERY-2 — Basic, the default, is picked until the studio picks
  // another; `null` stays possible so "Choose a design first" holds.
  const [template, setTemplate] = useState<GalleryTemplateId | null>(DEFAULT_TEMPLATE_ID);

  // ── Step 2 (lifted from the GAL-5/GAL-8 flow) ───────────────────
  const [files, setFiles] = useState<File[]>([]);
  const [galleryName, setGalleryName] = useState('');
  const [projectId, setProjectId] = useState<string>('');
  const [exifDate, setExifDate] = useState<string | null>(null);
  const [manualDate, setManualDate] = useState<string>('');
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [projects, setProjects] = useState<StudioProject[]>([]);
  const [dragOver, setDragOver] = useState(false);

  // ── Preview source (GAL-14) ─────────────────────────────────────
  // "we need to allow our customers to preview the designs with their
  // work or with the demo ones." Both previews — the 23 cards on step 1
  // and the live swatch on step 3 — already accept photos; nothing was
  // ever handing them the studio's own. This is that, plus the switch.
  const [previewSource, setPreviewSource] = useState<'sample' | 'mine'>('sample');
  const [previewPhotos, setPreviewPhotos] = useState<ThumbPhoto[]>([]);

  // ── Step 3 ──────────────────────────────────────────────────────
  const [customization, setCustomization] = useState<GalleryCustomization>({});
  const [pin, setPin] = useState('');
  const [publishNow, setPublishNow] = useState(false);

  const [creating, setCreating] = useState(false);

  // GALLERY-ONE — `getGalleryTheme`, never a raw index. GALLERY_THEMES
  // has one key now, so a gallery row still carrying a retired slug
  // ('editorial', 'premiere', …) indexed to undefined and the next
  // `theme.number` took the page down. The migration that rewrites
  // those rows is written but not applied, and even once it is, this
  // lookup should not depend on it.
  const theme = template ? getGalleryTheme(template) : null;

  // Reset on close; seed on open.
  useEffect(() => {
    if (open) {
      setStep(initialTemplate && isTemplateBuilt(initialTemplate) ? 2 : 1);
      setTemplate(initialTemplate && isTemplateBuilt(initialTemplate) ? initialTemplate : DEFAULT_TEMPLATE_ID);
      setProjectId(initialProjectId ?? '');
      return;
    }
    setStep(1);
    setTemplate(DEFAULT_TEMPLATE_ID);
    setFiles([]);
    setGalleryName('');
    setProjectId('');
    setExifDate(null);
    setManualDate('');
    setSuggestionDismissed(false);
    setDragOver(false);
    setCustomization({});
    setPin('');
    setPublishNow(false);
    setPreviewSource('sample');
  }, [open, initialTemplate, initialProjectId]);

  /**
   * GAL-14 — the studio's own photos as preview material.
   *
   * Object URLs, measured once so the designs that lay out by aspect
   * ratio (the contact sheet, the masonry grid) get real proportions
   * instead of guessing at squares. The cleanup revokes every URL it
   * created: without it, a studio who re-picks photos four times while
   * choosing a design leaves four full sets of decoded bitmaps pinned
   * in memory for as long as the tab lives.
   */
  useEffect(() => {
    const picked = files.filter(isPreviewableImage).slice(0, PREVIEW_PHOTO_LIMIT);
    if (picked.length === 0) {
      setPreviewPhotos([]);
      return;
    }
    let cancelled = false;
    const urls = picked.map((f) => URL.createObjectURL(f));
    void (async () => {
      const measured = await Promise.all(
        urls.map(
          (url, i) =>
            new Promise<ThumbPhoto>((resolve) => {
              const img = new Image();
              const base = { id: `own-${i}`, url, name: picked[i].name };
              img.onload = () =>
                resolve({ ...base, width: img.naturalWidth, height: img.naturalHeight });
              // Decode failed (a .jpg that isn't one, a truncated file).
              // Still previewable in principle — let the design lay it
              // out without dimensions rather than dropping the frame.
              img.onerror = () => resolve(base);
              img.src = url;
            }),
        ),
      );
      if (!cancelled) setPreviewPhotos(measured);
    })();
    return () => {
      cancelled = true;
      urls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [files]);

  // Autofocus the name once step 2 mounts.
  useEffect(() => {
    if (!open || step !== 2) return;
    const t = window.setTimeout(() => nameInputRef.current?.focus(), 80);
    return () => window.clearTimeout(t);
  }, [open, step]);

  const matchDate = exifDate ?? (manualDate ? new Date(manualDate).toISOString() : null);
  const { data: liveMatches } = useNovaProjectMatch(isDemo ? null : matchDate);
  const demoMatches = useMemo<ProjectMatch[]>(() => {
    if (!isDemo || !matchDate) return [];
    const target = new Date(matchDate).getTime();
    const oneDay = 86400000;
    return demoStore.projects
      .list()
      .flatMap((p): ProjectMatch[] => {
        const shot = (p.scheduled_date ?? p.shoot_date) as string | null | undefined;
        if (!shot) return [];
        const diffMs = new Date(shot).getTime() - target;
        if (Math.abs(diffMs) > oneDay) return [];
        return [
          {
            id: p.id as string,
            name: (p.name as string) ?? 'Untitled project',
            client_name: (p.client as { name?: string } | undefined)?.name ?? null,
            scheduled_date: shot,
            day_diff: Math.round(diffMs / oneDay),
          },
        ];
      })
      .sort((a, b) => Math.abs(a.day_diff) - Math.abs(b.day_diff));
  }, [isDemo, matchDate, demoStore]);
  const matches = isDemo ? demoMatches : liveMatches;

  // Projects for the picker (demo cast under Preview Studio).
  useEffect(() => {
    if (!open) return;
    if (isDemo) {
      setProjects(
        demoStore.projects.list().map((p) => ({
          id: p.id as string,
          name: (p.name as string) ?? 'Untitled project',
          scheduled_date: (p.scheduled_date as string | null) ?? null,
          clients: {
            name:
              (p.client as { name?: string } | undefined)?.name ??
              demoStore.clients.list().find((c) => c.id === p.client_id)?.name ??
              null,
          },
        })) as StudioProject[],
      );
      return;
    }
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from('projects')
        .select('id, name, scheduled_date, clients:client_id(name)')
        .order('scheduled_date', { ascending: false, nullsFirst: false })
        .limit(50);
      setProjects((data ?? []) as unknown as StudioProject[]);
    })();
  }, [open, user, isDemo, demoStore]);

  const handleFilesPicked = async (list: FileList | File[]) => {
    const picked = Array.from(list).filter(
      (f) => f.type.startsWith('image/') || isVideoFile(f),
    );
    const heldVideos = VIDEO_SHIPPED ? 0 : picked.filter(isVideoFile).length;
    const arr = VIDEO_SHIPPED ? picked : picked.filter((f) => !isVideoFile(f));
    if (heldVideos > 0) toast.info(VIDEO_HELD_MESSAGE);
    if (arr.length === 0) {
      if (heldVideos === 0) {
        toast.error(VIDEO_SHIPPED ? 'Pick some photos or videos to upload' : 'Pick some photos to upload');
      }
      return;
    }
    setFiles(arr);
    const firstImage = arr.find((f) => f.type.startsWith('image/'));
    const iso = firstImage ? await readExifDateFromFile(firstImage) : null;
    setExifDate(iso);
    if (iso && !manualDate) setManualDate(format(new Date(iso), 'yyyy-MM-dd'));
    setSuggestionDismissed(false);
    // They just handed us their work — show them their work. The
    // sample album is the fallback, not the thing they came to see.
    if (arr.some(isPreviewableImage)) setPreviewSource('mine');
  };

  const acceptMatch = (match: ProjectMatch) => {
    setProjectId(match.id);
    if (!galleryName.trim()) setGalleryName(match.name);
    setSuggestionDismissed(true);
  };

  // ── Create ──────────────────────────────────────────────────────
  const create = async () => {
    if (!template || !isTemplateBuilt(template)) {
      setStep(1);
      toast.error('Choose a design first');
      return;
    }
    // GAL-14 — a project is optional now, EXCEPT before the migration
    // that lets the column go null. With the flag off this is exactly
    // the old guard; with it on, no project is a legitimate answer.
    if (!STANDALONE_GALLERIES && !projectId) {
      setStep(2);
      toast.error('Pick a project to attach this gallery to');
      return;
    }
    if (pin && !/^\d{4}$/.test(pin)) {
      toast.error('The access code needs exactly four digits');
      return;
    }

    const insert = {
      project_id: projectId || null,
      // GAL-14 — post-migration `galleries.org_id` is NOT NULL with no
      // default, so a standalone row has nothing to inherit its owner
      // from. Sent ONLY behind the flag: before the migration the
      // column does not exist and PostgREST rejects the whole insert
      // (PGRST204), which would break attached galleries too.
      ...(STANDALONE_GALLERIES && orgId ? { org_id: orgId } : {}),
      name: galleryName.trim() || 'Gallery',
      template,
      customization,
      is_public: publishNow,
      ...(pin ? { access_pin: pin } : {}),
    };

    // ── DEMO-4 — Preview Studio create (session store only) ───────
    if (isDemo) {
      setCreating(true);
      try {
        const gallery = await createGallery.mutateAsync(insert);
        const images = files.filter(
          (f) => !isVideoFile(f) && (f.type.startsWith('image/') || /\.heic$/i.test(f.name)),
        );
        const skippedVideos = files.filter(isVideoFile).length;
        if (skippedVideos > 0) {
          toast.info(`Video stays out of Preview Studio — ${skippedVideos} file${skippedVideos === 1 ? '' : 's'} skipped.`);
        }
        const failures: string[] = [];
        for (let i = 0; i < images.length; i += 1) {
          const file = images[i];
          try {
            let blob: Blob = file;
            if (/\.heic$/i.test(file.name)) {
              const heic2any = (await import('heic2any')).default;
              const converted = await heic2any({ blob, toType: 'image/jpeg', quality: 0.92 });
              blob = Array.isArray(converted) ? converted[0] : (converted as Blob);
            }
            const objectUrl = URL.createObjectURL(blob);
            const dims = await new Promise<{ width: number; height: number } | null>((resolve) => {
              const img = new Image();
              img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
              img.onerror = () => resolve(null);
              img.src = objectUrl;
            });
            await addPhoto.mutateAsync({
              gallery_id: gallery.id,
              file_url: objectUrl,
              thumbnail_url: objectUrl,
              name: file.name,
              sort_order: i,
              width: dims?.width ?? null,
              height: dims?.height ?? null,
            });
          } catch (err) {
            console.error('[DEMO-4] preview upload failed', file.name, err);
            failures.push(file.name);
          }
        }
        if (failures.length > 0) {
          toast.warning(`Created "${gallery.name}", but ${failures.join(', ')} couldn't be read.`);
        } else {
          toast.success(
            images.length > 0
              ? `Created "${gallery.name}" with ${images.length} photo${images.length === 1 ? '' : 's'}.`
              : `Created "${gallery.name}" — drop photos in the editor.`,
          );
        }
        onClose();
        navigate(`/galleries/${gallery.id}`);
      } catch (err) {
        toast.error((err as Error).message);
      } finally {
        setCreating(false);
      }
      return;
    }

    if (!user || !orgId) {
      toast.error('Not signed in');
      return;
    }
    setCreating(true);
    try {
      // Only worth a round-trip when there IS a project to verify.
      if (projectId) {
        const { data: projectRow, error: projectLookupError } = await supabase
          .from('projects')
          .select('id')
          .eq('id', projectId)
          .maybeSingle();
        if (projectLookupError) throw projectLookupError;
        if (!projectRow) {
          toast.error("That project isn't available anymore — pick another one and try again.");
          setProjectId('');
          setStep(2);
          return;
        }
      }
      const gallery = await createGallery.mutateAsync(insert);

      const imageFiles = files.filter((f) => f.type.startsWith('image/') && !isVideoFile(f));
      const pickedVideoFiles = files.filter((f) => isVideoFile(f));
      const canUploadVideo = VIDEO_SHIPPED && hasFeature('videoDelivery');
      if (pickedVideoFiles.length > 0 && !canUploadVideo) {
        if (VIDEO_SHIPPED) openUpgradeModal('videoDelivery');
        else toast.info(VIDEO_HELD_MESSAGE);
      }
      const videoFiles = canUploadVideo ? pickedVideoFiles : [];

      if (imageFiles.length > 0 || videoFiles.length > 0) {
        galleryUploads.enqueue({
          galleryId: gallery.id,
          galleryName: gallery.name,
          // GAL-14 — '' for a standalone gallery; the manager swaps in
          // a literal 'standalone' segment so the positional storage
          // path keeps its shape (see galleryUploadManager.enqueue).
          projectId,
          orgId,
          userId: user.id,
          startingSortOrder: 0,
          imageFiles,
          videoFiles,
        });
        toast.success(
          publishNow
            ? `"${gallery.name}" is live — photos are uploading in the background.`
            : `Created "${gallery.name}" — uploading in the background.`,
          { description: 'Feel free to keep working; the upload pill tracks progress.' },
        );
      } else {
        toast.success(
          publishNow
            ? `"${gallery.name}" is live — drop photos in the editor.`
            : `Created "${gallery.name}" — drop photos in the editor.`,
        );
      }

      onClose();
      navigate(`/galleries/${gallery.id}`);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  const topMatch = matches?.[0];
  const showSuggestion = !!topMatch && !suggestionDismissed && projectId !== topMatch.id;
  const vids = files.filter(isVideoFile).length;
  const imgs = files.length - vids;

  const canContinue =
    step === 1 ? !!template && isTemplateBuilt(template)
    // GAL-14 — step 2 asks for a name, a date and (now optionally) a
    // project. None of it is required to have a gallery, so nothing
    // here blocks Continue once the flag is on.
    : step === 2 ? (STANDALONE_GALLERIES || !!projectId)
    : true;

  const next = () => setStep((s) => (s < 3 ? ((s + 1) as Step) : s));
  const back = () => setStep((s) => (s > 1 ? ((s - 1) as Step) : s));

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !creating && onClose()}>
      <DialogContent
        className={cn(
          'max-w-none max-h-none w-screen h-[100dvh] rounded-none border-0 p-0 flex flex-col',
          'bg-background',
          darkScope,
        )}
        // GAL-14 — the design step accepts a drop too, now that it has a
        // reason to: "preview with my photos" is the first thing a
        // studio wants on that screen, and making them go forward a
        // step to drop and then back again is the opposite of that.
        onDragOver={(e) => { if (step <= 2) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (step <= 2 && e.dataTransfer.files?.length) void handleFilesPicked(e.dataTransfer.files);
        }}
      >
        <DialogTitle className="sr-only">New gallery</DialogTitle>

        {/* GAL-14 — mounted at dialog level, not inside step 2. The
            design step's "use my photos" opens this same picker, and a
            ref into an unmounted subtree is null: the button would have
            done nothing at all, silently. */}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={VIDEO_SHIPPED ? 'image/*,video/*' : 'image/*'}
          className="hidden"
          onChange={(e) => e.target.files && void handleFilesPicked(e.target.files)}
        />

        {/* ── Header: eyebrow + steps ─────────────────────────── */}
        <header className="shrink-0 border-b border-border/40">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-5 py-4 md:px-8">
            <div className="flex items-center gap-3">
              <p className="orbit-eyebrow">New gallery</p>
              {theme && step > 1 && (
                <span className="hidden items-center gap-1.5 rounded-full border border-border/50 bg-card px-2.5 py-0.5 text-[11px] text-foreground sm:inline-flex">
                  <span className="h-2 w-2 rounded-full ring-1 ring-border/60" style={{ background: theme.accent }} aria-hidden />
                  {theme.name}
                </span>
              )}
            </div>
            <ol className="flex items-center gap-1 sm:gap-2" aria-label="Steps">
              {STEPS.map((s) => {
                const done = step > s.n;
                const active = step === s.n;
                return (
                  <li key={s.n} className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => { if (done) setStep(s.n); }}
                      disabled={!done}
                      className={cn(
                        'flex items-center gap-2 rounded-full px-2.5 py-1 text-xs transition-colors',
                        active ? 'bg-foreground text-background' : done ? 'text-foreground hover:bg-muted/40' : 'text-muted-foreground',
                      )}
                    >
                      <span className={cn('flex h-4 w-4 items-center justify-center rounded-full text-[10px] tabular-nums', active ? 'bg-background/20' : done ? 'bg-foreground text-background' : 'border border-border/70')}>
                        {done ? <Check className="h-2.5 w-2.5" /> : s.n}
                      </span>
                      <span className="hidden sm:inline">{s.label}</span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </div>
        </header>

        {/* ── Body ────────────────────────────────────────────── */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="mx-auto max-w-6xl px-5 py-8 md:px-8 md:py-12">

            {/* GALLERY-ONE — this step used to be "Choose a design." over
                the picker for 24 designs. The designs are gone, so what
                was left on screen was a heading, a sentence and an empty
                page asking for a choice that no longer exists (Rennan,
                with a screenshot: "kill this page").

                There is one gallery now and the studio colours it, so
                this step has nothing to ask — it has something to say.
                The same row the marketing page runs, turning on its own,
                because the honest answer to "which design?" is "whichever
                one you make".

                The photo-source control went with it: it belongs on
                Customize, where the live preview it feeds actually is,
                and it is already mounted there. */}
            {step === 1 && (
              <div className="space-y-10">
                <div className="mx-auto max-w-2xl space-y-3 text-center">
                  <h2 className="font-editorial text-4xl leading-tight tracking-tight text-foreground md:text-5xl">
                    Make it yours.
                  </h2>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    Pick a colour — any colour — set the type, choose the cover, and it goes
                    out under your name, never ours. Your photographs show the way you shot
                    them: never cropped, never zoomed, never quietly squared off. Favorites,
                    downloads and an access code, built in.
                  </p>
                </div>
                {/* Not interactive: there is nothing here to choose. */}
                <GalleryCoverFlow interactive={false} />
              </div>
            )}

            {step === 2 && (
              <div className="mx-auto w-full max-w-2xl space-y-10">
                <div className="space-y-4">
                  <input
                    ref={nameInputRef}
                    value={galleryName}
                    onChange={(e) => setGalleryName(e.target.value)}
                    placeholder="Name your gallery."
                    className={cn(
                      'w-full bg-transparent border-0 outline-none focus:ring-0 p-0',
                      'font-editorial text-4xl md:text-6xl leading-tight tracking-tight',
                      'text-foreground placeholder:text-muted-foreground/40 caret-primary',
                    )}
                    aria-label="Gallery name"
                  />
                  <div className="flex items-center gap-2.5 text-muted-foreground">
                    <OrbitIcon name="calendar" fallback={CalendarDays} className="h-4 w-4 shrink-0" />
                    <input
                      type="date"
                      value={manualDate}
                      onChange={(e) => { setManualDate(e.target.value); setSuggestionDismissed(false); }}
                      className="bg-transparent border-0 outline-none text-sm text-foreground/90 [color-scheme:dark]"
                      aria-label="Shoot date"
                    />
                    {exifDate && <span className="text-[11px] text-muted-foreground/70">from the photos' EXIF</span>}
                  </div>
                </div>

                {files.length === 0 ? (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(
                      'w-full rounded-2xl border-2 border-dashed p-10 text-center transition-colors orbit-box-dark bg-card/40',
                      dragOver ? 'border-primary/70 bg-primary/[0.06]' : 'border-border/60 hover:border-primary/40',
                    )}
                  >
                    <span className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl border border-teal-300/[0.4] bg-teal-400/[0.18]">
                      <OrbitIcon name="square.and.arrow.up" fallback={Upload} className="h-5 w-5 text-teal-500 dark:text-teal-200" />
                    </span>
                    <p className="text-sm font-medium text-foreground">
                      {VIDEO_SHIPPED ? 'Drop photos & videos — or browse' : 'Drop photos — or browse'}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Optional. Nova reads when they were shot and suggests the right project. You can also add photos later in the editor.
                    </p>
                  </button>
                ) : (
                  <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border/50 orbit-box-dark bg-card p-4">
                    {imgs > 0 && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-teal-300/[0.4] bg-teal-400/[0.18] px-2.5 py-1 text-xs font-medium text-foreground">
                        <OrbitIcon name="photo" fallback={ImageIcon} className="h-3 w-3 text-teal-500 dark:text-teal-200" />
                        {imgs} photo{imgs === 1 ? '' : 's'}
                      </span>
                    )}
                    {vids > 0 && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-300/[0.4] bg-sky-400/[0.18] px-2.5 py-1 text-xs font-medium text-foreground">
                        <OrbitIcon name="film" fallback={Film} className="h-3 w-3 text-sky-500 dark:text-sky-200" />
                        {vids} video{vids === 1 ? '' : 's'}
                      </span>
                    )}
                    {exifDate && <span className="text-xs text-muted-foreground">shot {format(new Date(exifDate), 'MMM d, yyyy')}</span>}
                    <Button variant="ghost" size="sm" className="ml-auto rounded-full" onClick={() => fileInputRef.current?.click()} disabled={creating}>
                      Change
                    </Button>
                  </div>
                )}

                {showSuggestion && topMatch && (
                  <div className="space-y-3 rounded-2xl border border-primary/30 bg-primary/5 p-5">
                    <div className="flex items-start gap-3">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/20">
                        <OrbitIcon name="sparkles" fallback={Sparkles} className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1 text-sm leading-relaxed text-foreground">
                        <span className="font-semibold">Nova</span> — these were shot on{' '}
                        <span className="font-medium">{format(new Date(matchDate!), 'MMM d')}</span>, around the same time as{' '}
                        <span className="font-medium">"{topMatch.name}"</span>
                        {topMatch.client_name ? ` (${topMatch.client_name})` : ''}. Attach to that project?
                      </div>
                      <button type="button" onClick={() => setSuggestionDismissed(true)} className="text-muted-foreground hover:text-foreground" aria-label="Dismiss suggestion">
                        <OrbitIcon name="xmark" fallback={X} className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={() => acceptMatch(topMatch)} className="gap-1.5 rounded-full">
                        <OrbitIcon name="sparkles" fallback={Sparkles} className="h-3.5 w-3.5" />
                        Attach to {topMatch.name}
                      </Button>
                      <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setSuggestionDismissed(true)}>
                        No, I'll pick
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-1.5">
                  <div className="flex items-baseline justify-between gap-3">
                    <Label className="orbit-eyebrow font-normal">Attach to project</Label>
                    {STANDALONE_GALLERIES && (
                      <span className="text-[11px] text-muted-foreground">Optional</span>
                    )}
                  </div>
                  <Select
                    value={projectId || (STANDALONE_GALLERIES ? NO_PROJECT : '')}
                    onValueChange={(v) => setProjectId(v === NO_PROJECT ? '' : v)}
                  >
                    <SelectTrigger className="rounded-xl">
                      <SelectValue placeholder="Pick a project" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      {STANDALONE_GALLERIES && (
                        <SelectItem value={NO_PROJECT}>No project</SelectItem>
                      )}
                      {projects.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name}
                          {p.clients?.name ? ` · ${p.clients.name}` : ''}
                          {p.scheduled_date ? ` · ${format(asDay(p.scheduled_date), 'MMM d, yyyy')}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {STANDALONE_GALLERIES && (
                    <p className="text-xs text-muted-foreground">
                      A gallery stands on its own. Attach it if this shoot belongs to a
                      project — you can also do it later, from the project.
                    </p>
                  )}
                </div>
              </div>
            )}

            {step === 3 && theme && (
              // `gal-customize` repoints --primary at Orbit blue for this
              // whole step (library.css). It was on GalleryCustomizeControls
              // alone, which left Publish now — a sibling card, not a
              // child — still wearing the Googie mustard.
              <div className="gal-customize grid gap-10 lg:grid-cols-[minmax(0,1fr)_360px]">
                <div className="space-y-8">
                  <div className="max-w-xl space-y-2">
                    <h2 className="font-editorial text-4xl leading-tight tracking-tight text-foreground md:text-5xl">
                      Make it yours.
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Background, type, the cover. Leave it alone and it stays clean.
                    </p>
                  </div>

                  <div className="rounded-2xl border border-border/50 orbit-box-dark bg-card p-5">
                    <GalleryCustomizeControls theme={theme} value={customization} onChange={setCustomization} brandColor={brandColor} brandLogoUrl={orgLogoUrl} disabled={creating} />
                  </div>

                  <div className="rounded-2xl border border-border/50 orbit-box-dark bg-card p-5">
                    <AccessCodeField mode="draft" value={pin} onChange={setPin} disabled={creating} />
                  </div>

                  <div className="flex items-center justify-between gap-4 rounded-2xl border border-border/50 orbit-box-dark bg-card p-5">
                    <div>
                      <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                        <OrbitIcon name="globe" fallback={Globe} className="h-3.5 w-3.5 text-emerald-400" />
                        Publish now
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {files.length > 0
                          ? 'The link goes live immediately; photos appear as they finish uploading.'
                          : 'The link goes live immediately, empty until you add photos. You can also publish from the editor.'}
                      </p>
                    </div>
                    <Switch checked={publishNow} onCheckedChange={setPublishNow} disabled={creating} />
                  </div>
                </div>

                <div className="lg:sticky lg:top-6 lg:self-start space-y-3">
                  <p className="orbit-eyebrow">Preview</p>
                  <PreviewSourceRow
                    source={previewSource}
                    onSource={setPreviewSource}
                    hasOwn={previewPhotos.length > 0}
                    onPickFiles={() => fileInputRef.current?.click()}
                    disabled={creating}
                    compact
                  />
                  <DesignPreview
                    theme={theme}
                    name={galleryName}
                    customization={customization}
                    brandColor={brandColor}
                    studioName={org?.name ?? null}
                    logoUrl={orgLogoUrl}
                    photos={previewSource === 'mine' ? previewPhotos : undefined}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────── */}
        <footer className="shrink-0 border-t border-border/40 bg-background">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-5 py-4 md:px-8">
            <div>
              {step > 1 ? (
                <Button variant="ghost" className="rounded-full gap-1.5" onClick={back} disabled={creating}>
                  <OrbitIcon name="arrow.left" fallback={ArrowLeft} className="h-4 w-4" />
                  Back
                </Button>
              ) : (
                <Button variant="ghost" className="rounded-full" onClick={onClose} disabled={creating}>
                  Cancel
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              {step < 3 ? (
                <Button size="lg" onClick={next} disabled={!canContinue || creating} className="gap-2 rounded-full px-6">
                  Continue
                  <OrbitIcon name="arrow.right" fallback={ArrowRight} className="h-4 w-4" />
                </Button>
              ) : (
                <Button size="lg" onClick={() => void create()} disabled={creating} className="gap-2 rounded-full px-6">
                  {creating ? <NovaSpinner size={16} /> : <OrbitIcon name="checkmark" fallback={Check} className="h-4 w-4" />}
                  {files.length > 0 ? (publishNow ? 'Create, upload & publish' : 'Create + upload') : (publishNow ? 'Create & publish' : 'Create gallery')}
                </Button>
              )}
            </div>
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
}


/**
 * GAL-14 — "preview the designs with their work or with the demo ones."
 *
 * One switch, shown on both the design step and the customize step, so
 * the answer follows the studio through the flow instead of being asked
 * twice. With no photos picked yet the "My photos" side is an upload
 * button rather than a dead option — the studio asking to see their own
 * work is exactly the moment to let them hand it over, and the file
 * they pick is the same `files` state the gallery is created from, so
 * nothing is uploaded twice.
 */
function PreviewSourceRow({
  source,
  onSource,
  hasOwn,
  onPickFiles,
  disabled = false,
  compact = false,
}: {
  source: 'sample' | 'mine';
  onSource: (s: 'sample' | 'mine') => void;
  hasOwn: boolean;
  onPickFiles: () => void;
  disabled?: boolean;
  compact?: boolean;
}) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', !compact && 'pb-1')}>
      {!compact && (
        <span className="mr-1 text-xs text-muted-foreground">Preview with</span>
      )}
      <div className="inline-flex rounded-full border border-border/60 bg-card p-0.5">
        <button
          type="button"
          onClick={() => onSource('sample')}
          disabled={disabled}
          aria-pressed={source === 'sample'}
          className={cn(
            'rounded-full px-3 py-1 text-xs transition-colors',
            source === 'sample'
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          Sample photos
        </button>
        <button
          type="button"
          onClick={() => (hasOwn ? onSource('mine') : onPickFiles())}
          disabled={disabled}
          aria-pressed={source === 'mine' && hasOwn}
          className={cn(
            'rounded-full px-3 py-1 text-xs transition-colors',
            source === 'mine' && hasOwn
              ? 'bg-foreground text-background'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          My photos
        </button>
      </div>
      {!hasOwn && (
        <button
          type="button"
          onClick={onPickFiles}
          disabled={disabled}
          className="text-xs text-muted-foreground underline underline-offset-4 hover:text-foreground"
        >
          Choose photos
        </button>
      )}
    </div>
  );
}

export default NewGalleryDialog;
